import { useEffect, useState } from "react";
import api from "../lib/api";
import { Bell, AlertTriangle, CheckCircle2, X, Trash2, Zap, Eye } from "lucide-react";
import { toast } from "sonner";

const SEVERITY = {
  info: { color: "#00D4FF", icon: Bell },
  warning: { color: "#F59E0B", icon: AlertTriangle },
  critical: { color: "#EF4444", icon: AlertTriangle },
};

export default function AlertsCenter() {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [scanning, setScanning] = useState(false);

  const refresh = async () => { try { setAlerts(await api.crmListAlerts()); } catch {} };
  useEffect(() => { refresh(); }, []);

  const scan = async () => {
    setScanning(true);
    try { const r = await api.crmScanAlerts(); toast.success(`Scan terminé · ${r.created} alertes`); refresh(); }
    catch { toast.error("Erreur scan"); }
    finally { setScanning(false); }
  };

  const markRead = async (id) => { await api.crmReadAlert(id); refresh(); };
  const resolve = async (id) => { await api.crmResolveAlert(id); refresh(); };
  const remove = async (id) => { await api.crmDeleteAlert(id); refresh(); };

  const filtered = alerts.filter(a => {
    if (filter === "unread") return !a.is_read;
    if (filter === "unresolved") return !a.is_resolved;
    if (filter === "critical") return a.severity === "critical";
    return true;
  });

  return (
    <div className="space-y-7 animate-fadeUp" data-testid="alerts-center-page">
      <header className="border-l-2 border-[#CCFF00] pl-5 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// CENTRE D'ALERTES</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Alertes intelligentes</h1>
          <p className="text-zinc-400 mt-2">Détection auto : chutes reach, comptes en baisse, reels viraux, tokens expirés, objectifs non atteints.</p>
        </div>
        <button onClick={scan} disabled={scanning} data-testid="scan-alerts-btn"
          className="bg-[#CCFF00] text-black font-medium px-5 py-2.5 rounded-sm hover:bg-[#B3E600] disabled:opacity-50 flex items-center gap-2 text-sm glow-volt">
          <Zap className="w-4 h-4" /> {scanning ? "Scan en cours..." : "Lancer un scan"}
        </button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total" v={alerts.length} />
        <Stat label="Non lues" v={alerts.filter(a => !a.is_read).length} color="#00D4FF" />
        <Stat label="Non résolues" v={alerts.filter(a => !a.is_resolved).length} color="#F59E0B" />
        <Stat label="Critiques" v={alerts.filter(a => a.severity === "critical").length} color="#EF4444" />
      </div>

      <div className="border-b border-[#1E1E24] flex gap-0">
        {[["all", "Toutes"], ["unread", "Non lues"], ["unresolved", "Non résolues"], ["critical", "Critiques"]].map(([id, label]) => (
          <button key={id} data-testid={`filter-${id}`} onClick={() => setFilter(id)}
            className={`px-4 py-2.5 text-xs font-mono uppercase tracking-wider transition border-b-2 ${
              filter === id ? "text-[#CCFF00] border-[#CCFF00]" : "text-zinc-500 border-transparent hover:text-zinc-300"
            }`}>{label}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-[#1E1E24] rounded-sm p-12 text-center">
          <Bell className="w-12 h-12 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
          <div className="text-zinc-500 text-sm">Aucune alerte. Tout va bien.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((a, i) => {
            const meta = SEVERITY[a.severity] || SEVERITY.info;
            const I = meta.icon;
            return (
              <div key={a.id} data-testid={`alert-${i}`}
                className={`border rounded-sm p-4 flex items-start gap-4 transition ${
                  !a.is_read ? "bg-[#0A0A0C]" : "bg-transparent opacity-60"
                }`} style={{ borderColor: meta.color + "30" }}>
                <I className="w-5 h-5 shrink-0 mt-0.5" style={{ color: meta.color }} strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <div className="text-white text-sm font-medium">{a.title}</div>
                    <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: meta.color + "20", color: meta.color }}>{a.severity}</span>
                    {a.is_resolved && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded-sm">RÉSOLU</span>}
                  </div>
                  {a.body && <div className="text-xs text-zinc-400 mb-2">{a.body}</div>}
                  <div className="text-[10px] font-mono text-zinc-600">{a.type} · {new Date(a.created_at).toLocaleString("fr-FR")}</div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {!a.is_read && (
                    <button onClick={() => markRead(a.id)} title="Marquer comme lu" className="text-zinc-500 hover:text-[#00D4FF] p-1">
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {!a.is_resolved && (
                    <button onClick={() => resolve(a.id)} title="Résoudre" className="text-zinc-500 hover:text-green-400 p-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => remove(a.id)} title="Supprimer" className="text-zinc-500 hover:text-red-400 p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, v, color }) => (
  <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4">
    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
    <div className="font-mono text-3xl font-bold" style={{ color: color || "#FFF" }}>{v}</div>
  </div>
);
